import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon, ShoppingBagIcon, WrenchIcon } from "lucide-react";
import { formatDateLocal } from "@/shared/lib/date-time";
import { getInitials } from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import type { DisputeListItem } from "../api/client";

const STATUS_OPTIONS: Record<
  string,
  {
    label: string;
    variant: "success" | "destructive" | "secondary" | "default";
  }
> = {
  open: { label: "ເປີດ", variant: "default" },
  investigating: { label: "ກຳລັງກວດສອບ", variant: "secondary" },
  resolved: { label: "ຕັດສິນແລ້ວ", variant: "success" },
  rejected: { label: "ປະຕິເສດ", variant: "destructive" },
};

const TYPE_LABELS: Record<string, string> = {
  order: "ອໍເດີ",
  job: "ວຽກ",
};

type DisputesTableProps = {
  isLoading: boolean;
  data: DisputeListItem[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onView: (row: DisputeListItem) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function DisputesTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onView,
  onPaginationChange,
  onSortingChange,
}: DisputesTableProps) {
  const columns: TanstackReactTable.ColumnDef<DisputeListItem>[] = [
    createSortableColumn<DisputeListItem>("type", "ປະເພດ", {
      size: 100,
      cell: ({ row }) =>
        row.original.type === "order" ? (
          <span className="flex items-center gap-2 text-sm">
            <ShoppingBagIcon className="size-4" />
            ອໍເດີ
          </span>
        ) : (
          <span className="flex items-center gap-2 text-sm">
            <WrenchIcon className="size-4" />
            ວຽກ
          </span>
        ),
    }),
    {
      id: "referenceId",
      header: "ID #",
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {row.original.referenceId
            ? row.original.referenceId.slice(0, 8).toUpperCase()
            : "—"}
        </span>
      ),
      size: 120,
    },
    {
      id: "openedByName",
      header: "ຜູ້ເປີດ",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage
              src={
                resolveImageSrc(row.original.openedByImage ?? null) ?? undefined
              }
              alt={row.original.openedByName ?? "avatar"}
            />
            <AvatarFallback className="bg-muted font-medium text-muted-foreground text-xs">
              {getInitials(row.original.openedByName ?? row.original.openedBy)}
            </AvatarFallback>
          </Avatar>
          <span className="truncate">
            {row.original.openedByName ?? row.original.openedBy}
          </span>
        </div>
      ),
      size: 180,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const status = row.original.status as keyof typeof STATUS_OPTIONS;
        const opt = STATUS_OPTIONS[status] ?? {
          label: status,
          variant: "secondary" as const,
        };
        return <Badge variant={opt.variant}>{opt.label}</Badge>;
      },
      size: 120,
    },
    {
      id: "reason",
      header: "ເຫດຜົນ",
      cell: ({ row }) => (
        <span className="line-clamp-1 text-sm">
          {row.original.reason ?? "—"}
        </span>
      ),
      size: 200,
    },
    createSortableColumn<DisputeListItem>("createdAt", "ວັນທີ", {
      size: 140,
      cell: ({ row }) => formatDateLocal(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "ການດຳເນີນການ",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງລາຍລະອຽດ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => onView(row.original),
            },
          ]}
          maxInline={2}
        />
      ),
      size: 140,
    },
  ];

  return (
    <DataTable<DisputeListItem, unknown>
      noDataMessage="ບໍ່ພົບຂໍ້ພິພາດ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting[0]?.id) return;
        onSortingChange(sorting[0].id as string, !!sorting[0].desc);
      }}
    />
  );
}
